import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  PickList,
  PickListModule,
  PickListSourceSelectEvent,
} from 'primeng/picklist';
import { CategoryStore } from '../../../stores/category/category.state';
import { Category } from '../../../models/category';
import { BadgeModule } from 'primeng/badge';
import { DividerModule } from 'primeng/divider';
import { SharedModule } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { NgIf } from '@angular/common';
import { CategoryFilterComponent } from '../category-filter/category-filter.component';
import { Indicator } from '../../../models/indicator';

@Component({
  selector: 'app-category-pick-list',
  standalone: true,
  imports: [
    BadgeModule,
    DividerModule,
    PickListModule,
    SharedModule,
    TagModule,
    NgIf,
    CategoryFilterComponent,
  ],
  templateUrl: './category-pick-list.component.html',
  styleUrl: './category-pick-list.component.scss',
})
export class CategoryPickListComponent implements OnInit, OnDestroy {
  @ViewChild('listPickerCtrl') listPickerCtrl!: PickList;
  @Input() selectedIds?: string[];
  @Input() ignoreChildren = false;
  @Input() keepSelection = false;
  @Input() autoselectParents = false;
  @Input() autoselectChildren = false;
  @Input() showSourceControls = false;
  @Input() showTargetControls = true;
  @Output() selectionChanged = new EventEmitter<string[]>();

  readonly categoryStore = inject(CategoryStore);

  selected: Category[] = [];

  /**
   * Angular lifecycle hook that is called after the component has been initialized.
   * It sets the `selected` property to the current selection from the category store.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    if (this.selectedIds) {
      this.selected = this.categoryStore.selectMultiple(this.selectedIds);
    } else {
      this.selected = this.categoryStore.selection();
    }
  }

  /**
   * Updates the selection of items in the category store and, if autoselectParents is enabled, adds parent items to the selection and updates the local selection state.
   *
   * @return {void} This method does not return a value.
   */
  updateSelection(): void {
    this.categoryStore.updateSelection(this.selected);
    if (this.autoselectParents) {
      this.categoryStore.addParentsToSelection();
      this.selected = this.categoryStore.selection();
    }
    if (this.autoselectChildren) {
      this.categoryStore.addChildrenToSelection();
      this.selected = this.categoryStore.selection();
    }

    this.selectionChanged.emit(this.selected.map(category => category._id));
  }

  /**
   * Handles the selection event and checks for specified hooks to automatically select parent or child categories.
   *
   * @param {PickListSourceSelectEvent} event - The selection event containing the items selected in the pick list.
   * @return {void} This method does not return a value.
   */
  checkForSelectionHooks(event: PickListSourceSelectEvent): void {
    if (this.autoselectParents) {
      event.items.forEach((category: Category) => {
        this.selectParents(category);
      });
    }

    if (this.autoselectChildren) {
      event.items.forEach((category: Category) => {
        this.selectChildren(category);
      });
    }
  }

  /**
   * Selects the children of a given category and updates the selected items list with unique entries.
   *
   * @param {Category} category - The category whose child items need to be selected.
   * @return {void} This method does not return a value.
   */
  private selectChildren(category: Category): void {
    const uniqueItems = Array.from(
      new Set([
        ...this.listPickerCtrl.selectedItemsSource,
        ...this.categoryStore.fetchCategoryChildren(category._id),
      ])
    );
    this.listPickerCtrl.selectedItemsSource = uniqueItems;
  }

  /**
   * Selects and updates the list of parent categories for a given category.
   *
   * @param {Category} category - The category for which the parent categories will be selected and added.
   * @return {void} This method does not return a value.
   */
  private selectParents(category: Category): void {
    const uniqueItems = Array.from(
      new Set([
        ...this.listPickerCtrl.selectedItemsSource,
        ...this.categoryStore.getParents(category._id),
      ])
    );
    this.listPickerCtrl.selectedItemsSource = uniqueItems;
  }

  /**
   * A lifecycle hook that is called when the component is destroyed.
   * This method checks the `keepSelection` property and, if it is false, clears the selection in the category store.
   *
   * @return {void} Does not return a value.
   */
  ngOnDestroy(): void {
    if (this.keepSelection) return;
    this.categoryStore.clearSelection();
  }
}
